/**
 * TopicBacklogSidebar — H.2 calendar companion.
 *
 * Lists `topic_backlog/*` filtered to status='ready' and groups by
 * `primaryPillarId` (Phase B.1 enrichment). Each topic renders a
 * DraggableTopicCard that the user drags onto a calendar slot — DayCell
 * recognises the custom MIME and creates / updates a publication.
 *
 * Pillar groups use the pillar's name + auto-derived color for visual
 * scanning. Topics without a primary pillar fall under "Unassigned".
 */

import React, { useMemo } from 'react';
import {
  Box,
  Paper,
  Stack,
  Typography,
  Divider,
  Chip,
  TextField,
  InputAdornment,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useTopicBacklog } from '../../hooks/useTopicBacklog';
import { usePillars } from '../../hooks/usePillars';
import { DraggableTopicCard } from './DraggableTopicCard';

const TESTID = 'topic-backlog-sidebar';

const PILLAR_COLORS = ['#1976d2', '#9c27b0', '#2e7d32', '#ed6c02', '#d32f2f', '#0288d1'];

export interface TopicBacklogSidebarProps {
  projectId: string;
  authorId?: string | null;
  /** Compact mode for narrower screens. Default false. */
  compact?: boolean;
  /** Overview mode renders cards without drag affordances. */
  readOnly?: boolean;
}

export const TopicBacklogSidebar: React.FC<TopicBacklogSidebarProps> = ({
  projectId,
  authorId,
  compact = false,
  readOnly = false,
}) => {
  const { topics, loading } = useTopicBacklog(projectId, authorId);
  const { pillars } = usePillars(projectId);
  const [search, setSearch] = React.useState('');

  const pillarById = useMemo(() => {
    const m: Record<string, { name: string; color: string }> = {};
    pillars.forEach((p, i) => {
      m[p.id] = { name: p.name ?? p.id, color: PILLAR_COLORS[i % PILLAR_COLORS.length] };
    });
    return m;
  }, [pillars]);

  const groups = useMemo(() => {
    const filtered = topics.filter((t) => {
      const okStatuses = ['draft', 'assigned'] as const;
      if (t.status && !okStatuses.includes(t.status as any)) return false;
      if (search.trim()) {
        const q = search.toLowerCase();
        return (
          (t.angle ?? '').toLowerCase().includes(q) ||
          ((t as any).keyMessage ?? '').toLowerCase().includes(q)
        );
      }
      return true;
    });
    const byPillar = new Map<string, typeof filtered>();
    for (const t of filtered) {
      const pid = (t as any).primaryPillarId ?? '__unassigned';
      const arr = byPillar.get(pid) ?? [];
      arr.push(t);
      byPillar.set(pid, arr);
    }
    return Array.from(byPillar.entries());
  }, [topics, search]);

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.5,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        minWidth: compact ? 220 : 280,
      }}
      data-testid={TESTID}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
        <Typography variant="subtitle2">Topic backlog</Typography>
        <Chip size="small" label={`${topics.length} total`} variant="outlined" />
      </Stack>
      <TextField
        size="small"
        placeholder="Search topics…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
        sx={{ mb: 1.5 }}
        inputProps={{ 'data-testid': `${TESTID}-search` }}
      />

      <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
        {loading ? (
          <Typography variant="caption" color="text.secondary">Loading backlog…</Typography>
        ) : groups.length === 0 ? (
          <Typography variant="caption" color="text.secondary">
            {search.trim() ? 'No topics match the search.' : 'No topics in backlog.'}
          </Typography>
        ) : (
          <Stack spacing={2} divider={<Divider flexItem />}>
            {groups.map(([pillarId, list]) => {
              const meta = pillarId === '__unassigned'
                ? { name: 'Unassigned', color: '#9e9e9e' }
                : pillarById[pillarId] ?? { name: pillarId, color: '#9e9e9e' };
              return (
                <Box key={pillarId} data-testid={`${TESTID}-group-${pillarId}`}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75 }}>
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        bgcolor: meta.color,
                      }}
                    />
                    <Typography variant="caption" fontWeight={600}>
                      {meta.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      ({list.length})
                    </Typography>
                  </Stack>
                  {list.map((t) => (
                    <DraggableTopicCard
                      key={t.id}
                      topic={t}
                      primaryPillarColor={meta.color}
                      primaryPillarName={meta.name === 'Unassigned' ? undefined : meta.name}
                      disabled={readOnly}
                    />
                  ))}
                </Box>
              );
            })}
          </Stack>
        )}
      </Box>
    </Paper>
  );
};

export default TopicBacklogSidebar;
